import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import {
  Plus,
  Search,
  Mail,
  Edit,
  Copy,
  Trash2,
  X,
  Eye,
  Calendar,
  User,
  ChevronDown,
  Braces,
  LayoutGrid,
  List
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import { useToastNotification } from '@/contexts/ToastContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Default built-in placeholders
const DEFAULT_PLACEHOLDERS = [
  { key: 'first_name', display_name: 'First Name', sample: 'John' },
  { key: 'full_name', display_name: 'Full Name', sample: 'John Doe' },
  { key: 'org_name', display_name: 'Organization Name', sample: 'Acme Corp' },
  { key: 'owner_name', display_name: 'Owner Name', sample: 'Jane Smith' },
  { key: 'fund_name', display_name: 'Fund Name', sample: 'Venture Fund I' },
];

const formatDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  }).format(date);
};

const Templates = () => {
  const toast = useToastNotification();
  
  // View state
  const [currentView, setCurrentView] = useState('templates'); // 'templates' or 'placeholders'
  
  // Templates state
  const [templates, setTemplates] = useState([]);
  const [filteredTemplates, setFilteredTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Placeholders state
  const [customPlaceholders, setCustomPlaceholders] = useState([]);
  const [placeholdersLoading, setPlaceholdersLoading] = useState(true);
  const [allPlaceholders, setAllPlaceholders] = useState([]);
  
  // Modal states
  const [createTemplateModalOpen, setCreateTemplateModalOpen] = useState(false);
  const [editTemplateModalOpen, setEditTemplateModalOpen] = useState(false);
  const [deleteTemplateModalOpen, setDeleteTemplateModalOpen] = useState(false);
  const [previewTemplateModalOpen, setPreviewTemplateModalOpen] = useState(false);
  
  const [createPlaceholderModalOpen, setCreatePlaceholderModalOpen] = useState(false);
  const [editPlaceholderModalOpen, setEditPlaceholderModalOpen] = useState(false);
  const [deletePlaceholderModalOpen, setDeletePlaceholderModalOpen] = useState(false);
  
  // Form states
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [selectedPlaceholder, setSelectedPlaceholder] = useState(null);
  const [templateFormData, setTemplateFormData] = useState({
    name: '',
    channel: 'email',
    subject: '',
    body: ''
  });
  const [placeholderFormData, setPlaceholderFormData] = useState({
    key: '',
    display_name: '',
    sample_value: '',
    description: ''
  });

  // Fetch templates
  const fetchTemplates = async () => {
    try {
      setTemplatesLoading(true);
      const response = await axios.get(`${API_URL}/api/templates`, {
        withCredentials: true
      });
      setTemplates(response.data);
      setFilteredTemplates(response.data);
    } catch (error) {
      console.error('Error fetching templates:', error);
      toast.error('Failed to load templates');
    } finally {
      setTemplatesLoading(false);
    }
  };

  // Fetch custom placeholders
  const fetchPlaceholders = async () => {
    try {
      setPlaceholdersLoading(true);
      const response = await axios.get(`${API_URL}/api/placeholders`, {
        withCredentials: true
      });
      setCustomPlaceholders(response.data);
    } catch (error) {
      console.error('Error fetching placeholders:', error);
      toast.error('Failed to load placeholders');
    } finally {
      setPlaceholdersLoading(false);
    }
  };

  useEffect(() => {
    fetchTemplates();
    fetchPlaceholders();
  }, []);

  // Merge default and custom placeholders
  useEffect(() => {
    const merged = [
      ...DEFAULT_PLACEHOLDERS.map(p => ({ ...p, isDefault: true })),
      ...customPlaceholders.map(p => ({ ...p, isDefault: false }))
    ];
    setAllPlaceholders(merged);
  }, [customPlaceholders]);

  // Apply search filter to templates
  useEffect(() => {
    let filtered = templates;

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(t =>
        t.name.toLowerCase().includes(query) ||
        t.body.toLowerCase().includes(query)
      );
    }

    setFilteredTemplates(filtered);
  }, [templates, searchQuery]);

  // Replace placeholders for preview
  const replacePlaceholders = (text) => {
    if (!text) return '';
    let result = text;
    allPlaceholders.forEach(({ key, sample, sample_value }) => {
      const placeholder = `{{${key}}}`;
      const value = sample_value || sample || '';
      result = result.replace(new RegExp(placeholder.replace(/[{}]/g, '\\$&'), 'g'), value);
    });
    return result;
  };

  // Template CRUD operations
  const handleCreateTemplate = async () => {
    try {
      await axios.post(`${API_URL}/api/templates`, templateFormData, {
        withCredentials: true
      });
      toast.success('Template created successfully');
      setCreateTemplateModalOpen(false);
      fetchTemplates();
      resetTemplateForm();
    } catch (error) {
      console.error('Error creating template:', error);
      toast.error(error.response?.data?.detail || 'Failed to create template');
    }
  };

  const handleUpdateTemplate = async () => {
    try {
      await axios.patch(`${API_URL}/api/templates/${selectedTemplate.id}`, templateFormData, {
        withCredentials: true
      });
      toast.success('Template updated successfully');
      setEditTemplateModalOpen(false);
      fetchTemplates();
      resetTemplateForm();
    } catch (error) {
      console.error('Error updating template:', error);
      toast.error(error.response?.data?.detail || 'Failed to update template');
    }
  };

  const handleDeleteTemplate = async () => {
    try {
      await axios.delete(`${API_URL}/api/templates/${selectedTemplate.id}`, {
        withCredentials: true
      });
      toast.success('Template deleted successfully');
      setDeleteTemplateModalOpen(false);
      fetchTemplates();
      setSelectedTemplate(null);
    } catch (error) {
      console.error('Error deleting template:', error);
      toast.error('Failed to delete template');
    }
  };

  const handleDuplicateTemplate = async (template) => {
    try {
      await axios.post(`${API_URL}/api/templates/${template.id}/duplicate`, {}, {
        withCredentials: true
      });
      toast.success('Template duplicated successfully');
      fetchTemplates();
    } catch (error) {
      console.error('Error duplicating template:', error);
      toast.error('Failed to duplicate template');
    }
  };

  // Placeholder CRUD operations
  const handleCreatePlaceholder = async () => {
    try {
      await axios.post(`${API_URL}/api/placeholders`, placeholderFormData, {
        withCredentials: true
      });
      toast.success('Placeholder created successfully');
      setCreatePlaceholderModalOpen(false);
      fetchPlaceholders();
      resetPlaceholderForm();
    } catch (error) {
      console.error('Error creating placeholder:', error);
      toast.error(error.response?.data?.detail || 'Failed to create placeholder');
    }
  };

  const handleUpdatePlaceholder = async () => {
    try {
      const { key, ...updateData } = placeholderFormData;
      await axios.patch(`${API_URL}/api/placeholders/${selectedPlaceholder.id}`, updateData, {
        withCredentials: true
      });
      toast.success('Placeholder updated successfully');
      setEditPlaceholderModalOpen(false);
      fetchPlaceholders();
      resetPlaceholderForm();
    } catch (error) {
      console.error('Error updating placeholder:', error);
      toast.error(error.response?.data?.detail || 'Failed to update placeholder');
    }
  };

  const handleDeletePlaceholder = async () => {
    try {
      await axios.delete(`${API_URL}/api/placeholders/${selectedPlaceholder.id}`, {
        withCredentials: true
      });
      toast.success('Placeholder deleted successfully');
      setDeletePlaceholderModalOpen(false);
      fetchPlaceholders();
      setSelectedPlaceholder(null);
    } catch (error) {
      console.error('Error deleting placeholder:', error);
      toast.error('Failed to delete placeholder');
    }
  };

  // Helper functions
  const resetTemplateForm = () => {
    setTemplateFormData({
      name: '',
      channel: 'email',
      subject: '',
      body: ''
    });
    setSelectedTemplate(null);
  };

  const resetPlaceholderForm = () => {
    setPlaceholderFormData({
      key: '',
      display_name: '',
      sample_value: '',
      description: ''
    });
    setSelectedPlaceholder(null);
  };

  const openCreateTemplateModal = () => {
    resetTemplateForm();
    setCreateTemplateModalOpen(true);
  };

  const openEditTemplateModal = (template) => {
    setSelectedTemplate(template);
    setTemplateFormData({
      name: template.name,
      channel: template.channel,
      subject: template.subject || '',
      body: template.body
    });
    setEditTemplateModalOpen(true);
  };

  const openDeleteTemplateModal = (template) => {
    setSelectedTemplate(template);
    setDeleteTemplateModalOpen(true);
  };

  const openPreviewTemplateModal = (template) => {
    setSelectedTemplate(template);
    setPreviewTemplateModalOpen(true);
  };

  const openCreatePlaceholderModal = () => {
    resetPlaceholderForm();
    setCreatePlaceholderModalOpen(true);
  };

  const openEditPlaceholderModal = (placeholder) => {
    setSelectedPlaceholder(placeholder);
    setPlaceholderFormData({
      key: placeholder.key,
      display_name: placeholder.display_name,
      sample_value: placeholder.sample_value,
      description: placeholder.description || ''
    });
    setEditPlaceholderModalOpen(true);
  };

  const openDeletePlaceholderModal = (placeholder) => {
    setSelectedPlaceholder(placeholder);
    setDeletePlaceholderModalOpen(true);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
      className="space-y-6"
    >
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-heading font-semibold text-white">
            Message Templates
          </h1>
          <p className="text-zinc-400 mt-1">
            Create and manage email templates
          </p>
        </div>
        
        {/* Create Dropdown */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button>
              <Plus size={16} className="mr-2" />
              Create
              <ChevronDown size={14} className="ml-2" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={openCreateTemplateModal}>
              <Mail size={14} className="mr-2" />
              Template
            </DropdownMenuItem>
            <DropdownMenuItem onClick={openCreatePlaceholderModal}>
              <Braces size={14} className="mr-2" />
              Placeholder
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* View Toggle */}
      <div className="flex items-center gap-2 border-b border-white/6">
        <button
          onClick={() => setCurrentView('templates')}
          className={`px-4 py-2 text-sm font-medium transition-colors relative ${
            currentView === 'templates'
              ? 'text-white'
              : 'text-zinc-400 hover:text-zinc-300'
          }`}
        >
          <div className="flex items-center gap-2">
            <LayoutGrid size={16} />
            Templates
          </div>
          {currentView === 'templates' && (
            <motion.div
              layoutId="activeTab"
              className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-500"
            />
          )}
        </button>
        
        <button
          onClick={() => setCurrentView('placeholders')}
          className={`px-4 py-2 text-sm font-medium transition-colors relative ${
            currentView === 'placeholders'
              ? 'text-white'
              : 'text-zinc-400 hover:text-zinc-300'
          }`}
        >
          <div className="flex items-center gap-2">
            <Braces size={16} />
            Placeholders
          </div>
          {currentView === 'placeholders' && (
            <motion.div
              layoutId="activeTab"
              className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-500"
            />
          )}
        </button>
      </div>

      {/* Templates View */}
      {currentView === 'templates' && (
        <>
          {/* Filters */}
          <div className="flex items-center gap-3">
            <div className="relative flex-1 max-w-md">
              <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
              <Input
                placeholder="Search templates..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>

          {/* Templates Grid */}
          {templatesLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-zinc-400">Loading templates...</div>
            </div>
          ) : filteredTemplates.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <img src="/gmail-logo.svg" alt="Gmail" className="w-12 h-12 opacity-40 mb-4" />
              <h3 className="text-lg font-medium text-zinc-300 mb-1">No templates found</h3>
              <p className="text-zinc-500 mb-4">
                {searchQuery
                  ? 'Try adjusting your search'
                  : 'Create your first template to get started'}
              </p>
              {!searchQuery && (
                <Button onClick={openCreateTemplateModal}>
                  <Plus size={16} className="mr-2" />
                  Create Template
                </Button>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredTemplates.map((template) => (
                <motion.div
                  key={template.id}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="bg-[#0f1216] border border-white/6 rounded-lg p-4 hover:border-white/12 transition-colors"
                >
                  {/* Header */}
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <img src="/gmail-logo.svg" alt="Gmail" className="w-[18px] h-[18px]" />
                      <h3 className="font-medium text-white">{template.name}</h3>
                    </div>
                    
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                          <Edit size={14} />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => openPreviewTemplateModal(template)}>
                          <Eye size={14} className="mr-2" />
                          Preview
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => openEditTemplateModal(template)}>
                          <Edit size={14} className="mr-2" />
                          Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleDuplicateTemplate(template)}>
                          <Copy size={14} className="mr-2" />
                          Duplicate
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => openDeleteTemplateModal(template)}
                          className="text-red-400"
                        >
                          <Trash2 size={14} className="mr-2" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>

                  {/* Subject */}
                  {template.subject && (
                    <div className="mb-2">
                      <p className="text-xs text-zinc-500 mb-1">Subject:</p>
                      <p className="text-sm text-zinc-300 line-clamp-1">{template.subject}</p>
                    </div>
                  )}

                  {/* Body Preview */}
                  <div className="mb-3">
                    <p className="text-sm text-zinc-400 line-clamp-3">{template.body}</p>
                  </div>

                  {/* Footer */}
                  <div className="flex items-center justify-between text-xs text-zinc-500 pt-3 border-t border-white/6">
                    <div className="flex items-center gap-1">
                      <Calendar size={12} />
                      {formatDate(template.updatedAt)}
                    </div>
                    <div className="flex items-center gap-1">
                      <User size={12} />
                      {template.updatedBy}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Placeholders View */}
      {currentView === 'placeholders' && (
        <div className="space-y-4">
          {placeholdersLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-zinc-400">Loading placeholders...</div>
            </div>
          ) : (
            <>
              {/* Default Placeholders */}
              <div>
                <h3 className="text-sm font-medium text-zinc-400 mb-3">Default Placeholders</h3>
                <div className="space-y-2">
                  {DEFAULT_PLACEHOLDERS.map((placeholder) => (
                    <div
                      key={placeholder.key}
                      className="bg-[#0f1216] border border-white/6 rounded-lg p-4 flex items-center justify-between"
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-1">
                          <Badge variant="secondary" className="font-mono">
                            {`{{${placeholder.key}}}`}
                          </Badge>
                          <span className="text-sm font-medium text-white">{placeholder.display_name}</span>
                        </div>
                        <p className="text-xs text-zinc-500">Sample: {placeholder.sample}</p>
                      </div>
                      <Badge variant="outline" className="text-xs">Built-in</Badge>
                    </div>
                  ))}
                </div>
              </div>

              {/* Custom Placeholders */}
              <div>
                <h3 className="text-sm font-medium text-zinc-400 mb-3">Custom Placeholders</h3>
                {customPlaceholders.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12 text-center bg-[#0f1216] border border-white/6 rounded-lg">
                    <Braces size={48} className="text-zinc-600 mb-4" />
                    <h3 className="text-lg font-medium text-zinc-300 mb-1">No custom placeholders</h3>
                    <p className="text-zinc-500 mb-4">Create custom placeholders for your specific needs</p>
                    <Button onClick={openCreatePlaceholderModal}>
                      <Plus size={16} className="mr-2" />
                      Create Placeholder
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {customPlaceholders.map((placeholder) => (
                      <div
                        key={placeholder.id}
                        className="bg-[#0f1216] border border-white/6 rounded-lg p-4 flex items-center justify-between hover:border-white/12 transition-colors"
                      >
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-1">
                            <Badge variant="secondary" className="font-mono">
                              {`{{${placeholder.key}}}`}
                            </Badge>
                            <span className="text-sm font-medium text-white">{placeholder.display_name}</span>
                          </div>
                          <p className="text-xs text-zinc-500 mb-1">Sample: {placeholder.sample_value}</p>
                          {placeholder.description && (
                            <p className="text-xs text-zinc-600">{placeholder.description}</p>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openEditPlaceholderModal(placeholder)}
                          >
                            <Edit size={14} />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openDeletePlaceholderModal(placeholder)}
                            className="text-red-400 hover:text-red-300"
                          >
                            <Trash2 size={14} />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}

      {/* Create/Edit Template Modal */}
      <Dialog open={createTemplateModalOpen || editTemplateModalOpen} onOpenChange={(open) => {
        if (!open) {
          setCreateTemplateModalOpen(false);
          setEditTemplateModalOpen(false);
          resetTemplateForm();
        }
      }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {createTemplateModalOpen ? 'Create Template' : 'Edit Template'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Name */}
            <div>
              <Label htmlFor="name">Template Name*</Label>
              <Input
                id="name"
                value={templateFormData.name}
                onChange={(e) => setTemplateFormData({ ...templateFormData, name: e.target.value })}
                placeholder="e.g., Welcome Email"
                className="mt-1.5"
              />
            </div>

            {/* Channel */}
            {/* Channel - Fixed to Email */}
            <div>
              <Label htmlFor="channel">Channel</Label>
              <Input
                id="channel"
                value="Email"
                disabled
                className="mt-1.5 bg-zinc-900/50"
              />
            </div>

            {/* Subject */}
            <div>
              <Label htmlFor="subject">Subject*</Label>
              <Input
                id="subject"
                value={templateFormData.subject}
                onChange={(e) => setTemplateFormData({ ...templateFormData, subject: e.target.value })}
                placeholder="e.g., Welcome to {{org_name}}"
                className="mt-1.5"
              />
            </div>

            {/* Body */}
            <div>
              <Label htmlFor="body">Message Body*</Label>
              <Textarea
                id="body"
                value={templateFormData.body}
                onChange={(e) => setTemplateFormData({ ...templateFormData, body: e.target.value })}
                placeholder="Hi {{first_name}},&#10;&#10;Welcome to our platform!"
                rows={8}
                className="mt-1.5 font-mono text-sm"
              />
            </div>

            {/* Available Placeholders */}
            <div className="bg-[#0a0a0a] border border-white/6 rounded-lg p-4">
              <p className="text-sm font-medium text-white mb-2">Available Placeholders:</p>
              <div className="flex flex-wrap gap-2">
                {allPlaceholders.map((p) => (
                  <Badge key={p.key} variant="secondary" className="font-mono text-xs">
                    {`{{${p.key}}}`}
                  </Badge>
                ))}
              </div>
            </div>

            {/* Preview */}
            {(templateFormData.body || templateFormData.subject) && (
              <div className="bg-[#0a0a0a] border border-white/6 rounded-lg p-4">
                <p className="text-sm font-medium text-white mb-3">Preview (with sample data):</p>
                {templateFormData.channel === 'email' && templateFormData.subject && (
                  <div className="mb-3">
                    <p className="text-xs text-zinc-500 mb-1">Subject:</p>
                    <p className="text-sm text-zinc-300">{replacePlaceholders(templateFormData.subject)}</p>
                  </div>
                )}
                <div>
                  <p className="text-xs text-zinc-500 mb-1">Body:</p>
                  <p className="text-sm text-zinc-300 whitespace-pre-wrap">
                    {replacePlaceholders(templateFormData.body)}
                  </p>
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setCreateTemplateModalOpen(false);
                setEditTemplateModalOpen(false);
                resetTemplateForm();
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={createTemplateModalOpen ? handleCreateTemplate : handleUpdateTemplate}
              disabled={!templateFormData.name || !templateFormData.body || !templateFormData.subject}
            >
              {createTemplateModalOpen ? 'Create Template' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Template Modal */}
      <Dialog open={deleteTemplateModalOpen} onOpenChange={setDeleteTemplateModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Template</DialogTitle>
          </DialogHeader>
          <p className="text-zinc-400 py-4">
            Are you sure you want to delete <span className="text-white font-medium">{selectedTemplate?.name}</span>? 
            This action cannot be undone.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTemplateModalOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeleteTemplate}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Template Modal */}
      <Dialog open={previewTemplateModalOpen} onOpenChange={setPreviewTemplateModalOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <img src="/gmail-logo.svg" alt="Gmail" className="w-[18px] h-[18px]" />
              {selectedTemplate?.name}
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            {selectedTemplate?.subject && (
              <div>
                <p className="text-sm text-zinc-500 mb-2">Subject (with sample data):</p>
                <p className="text-base text-white">{replacePlaceholders(selectedTemplate.subject)}</p>
              </div>
            )}
            
            <div>
              <p className="text-sm text-zinc-500 mb-2">Body (with sample data):</p>
              <div className="bg-[#0a0a0a] border border-white/6 rounded-lg p-4">
                <p className="text-sm text-zinc-300 whitespace-pre-wrap">
                  {replacePlaceholders(selectedTemplate?.body || '')}
                </p>
              </div>
            </div>

            <div className="pt-4 border-t border-white/6 text-xs text-zinc-500 space-y-1">
              <p>Created: {formatDate(selectedTemplate?.createdAt)} by {selectedTemplate?.createdBy}</p>
              <p>Last Updated: {formatDate(selectedTemplate?.updatedAt)} by {selectedTemplate?.updatedBy}</p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setPreviewTemplateModalOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create/Edit Placeholder Modal */}
      <Dialog open={createPlaceholderModalOpen || editPlaceholderModalOpen} onOpenChange={(open) => {
        if (!open) {
          setCreatePlaceholderModalOpen(false);
          setEditPlaceholderModalOpen(false);
          resetPlaceholderForm();
        }
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {createPlaceholderModalOpen ? 'Create Custom Placeholder' : 'Edit Placeholder'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Key */}
            <div>
              <Label htmlFor="key">Placeholder Key*</Label>
              <Input
                id="key"
                value={placeholderFormData.key}
                onChange={(e) => setPlaceholderFormData({ ...placeholderFormData, key: e.target.value.toLowerCase().replace(/[^a-z_]/g, '') })}
                placeholder="e.g., deal_size"
                className="mt-1.5 font-mono"
                disabled={editPlaceholderModalOpen}
              />
              <p className="text-xs text-zinc-500 mt-1">
                Use lowercase letters and underscores only. Will be used as {`{{${placeholderFormData.key || 'key'}}}`}
              </p>
            </div>

            {/* Display Name */}
            <div>
              <Label htmlFor="display_name">Display Name*</Label>
              <Input
                id="display_name"
                value={placeholderFormData.display_name}
                onChange={(e) => setPlaceholderFormData({ ...placeholderFormData, display_name: e.target.value })}
                placeholder="e.g., Deal Size"
                className="mt-1.5"
              />
            </div>

            {/* Sample Value */}
            <div>
              <Label htmlFor="sample_value">Sample Value*</Label>
              <Input
                id="sample_value"
                value={placeholderFormData.sample_value}
                onChange={(e) => setPlaceholderFormData({ ...placeholderFormData, sample_value: e.target.value })}
                placeholder="e.g., $5M"
                className="mt-1.5"
              />
              <p className="text-xs text-zinc-500 mt-1">
                Used for preview in template editor
              </p>
            </div>

            {/* Description */}
            <div>
              <Label htmlFor="description">Description (optional)</Label>
              <Textarea
                id="description"
                value={placeholderFormData.description}
                onChange={(e) => setPlaceholderFormData({ ...placeholderFormData, description: e.target.value })}
                placeholder="e.g., Total investment amount for this deal"
                rows={3}
                className="mt-1.5"
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setCreatePlaceholderModalOpen(false);
                setEditPlaceholderModalOpen(false);
                resetPlaceholderForm();
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={createPlaceholderModalOpen ? handleCreatePlaceholder : handleUpdatePlaceholder}
              disabled={!placeholderFormData.key || !placeholderFormData.display_name || !placeholderFormData.sample_value}
            >
              {createPlaceholderModalOpen ? 'Create Placeholder' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Placeholder Modal */}
      <Dialog open={deletePlaceholderModalOpen} onOpenChange={setDeletePlaceholderModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Placeholder</DialogTitle>
          </DialogHeader>
          <p className="text-zinc-400 py-4">
            Are you sure you want to delete the placeholder <Badge variant="secondary" className="font-mono mx-1">{`{{${selectedPlaceholder?.key}}}`}</Badge>? 
            This action cannot be undone.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeletePlaceholderModalOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeletePlaceholder}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </motion.div>
  );
};

export default Templates;
